import { assertNotNull } from '../../../helpers/nullHelpers';
import { UnitHeatSourceTypeCode } from '../../shared/quote-or-design-form/quoteOrDesignFormData';
import { DataForDesignDrawingsManifold, DataForDesignDrawingsUnit } from './dataForDesignDrawingsResponse';
import { round, sumBy, range, ceil } from 'lodash';

export const getShortfallTextOrNull = (
  unit: DataForDesignDrawingsUnit,
  externalAirTemperatureInDegreesCelsius: number,
): string | null => {
  const shortfallText = unit.manifolds
    .filter(manifold => manifold.rooms != null)
    .map(manifold =>
      assertNotNull(manifold.rooms)
        .filter(room => room.heatLossAndHeatOutput != null && room.heatLossAndHeatOutput.shortfallInWatts > 0)
        .map(room =>
          shortfallTextForRoom(
            room.name,
            assertNotNull(room.heatLossAndHeatOutput).shortfallInWatts,
            externalAirTemperatureInDegreesCelsius,
          ),
        )
        .join('\n'),
    )
    .join('\n');
  return shortfallText.trim() === '' ? null : shortfallText;
};

const shortfallTextForRoom = (
  roomName: string,
  shortfallInWatts: number,
  externalAirTemperatureInDegreesCelsius: number,
): string =>
  `${roomName}
The ${roomName} has a shortfall of ${round(
    shortfallInWatts,
  )} Watts at an external temperature of ${externalAirTemperatureInDegreesCelsius} degrees.
`;

export const informationForCadLayoutText = (
  flowTemperatureInDegreesCelsius: number,
  flowRateInLitresPerMinute: number,
  indexPortKpa: number,
) =>
  `The system circulator has been supplied (unless it has been requested to be supplied by others) to provide the required UFH flow rate as designed.

Isolation valves are supplied for each manifold, part Code MCP-A and should be installed to allow isolation of manifolds for maintenance purposes

The pipe size shown is for illustrative purposes based on assumed pipe runs to each manifold, the table should be used to ensure that the water velocity does not exceed 1.5 m/s or be less than 0.5 m/s

Wiring centres are supplied (unless they are removed at the client's request). Wiring Centres allow for up to 8 zones to be connected, as a result in some situations more than 1 wiring centre will be provided per manifold.

A designed flow temperature of ${flowTemperatureInDegreesCelsius}°C is required.

Manifold Flow Rate: ${round(flowRateInLitresPerMinute, 2)} litres/minute

Index Port kPa: ${isNaN(indexPortKpa) ? '' : `${round(indexPortKpa, 2)}kPa`}
`;

export const installationSchedule = (manifold: DataForDesignDrawingsManifold): string | null => {
  const numberOfPorts = sumBy(manifold.rooms, room => room.numberOfPorts);
  const portNumbers = range(1, numberOfPorts + 1);

  const roomInstallationSchedules = manifold.rooms
    .filter(
      room =>
        room.numberOfPorts > 0 &&
        room.suppliedCoilLengthInMetres != null &&
        room.cutLengthInMetres != null &&
        room.pipeDiameterInMillimetres != null &&
        room.pipeSpacingInMillimetres != null &&
        room.heatLossAndHeatOutput != null,
    )
    .map(room =>
      installationScheduleForRoom({
        roomName: room.name,
        floorConstructionName: room.floorConstructionName,
        portNumbers: portNumbers.splice(0, room.numberOfPorts),
        numberOfPipes: room.numberOfPorts * room.numberOfPipesPerPort,
        manifoldName: manifold.name,
        suppliedCoilLengthInMetres: assertNotNull(room.suppliedCoilLengthInMetres),
        cutLengthInMetres: assertNotNull(room.cutLengthInMetres),
        pipeDiameterInMillimetres: assertNotNull(room.pipeDiameterInMillimetres),
        pipeSpacingInMillimetres: assertNotNull(room.pipeSpacingInMillimetres),
        flowRateInLitresPerMinute: assertNotNull(room.heatLossAndHeatOutput).flowRateInLitresPerMinute,
      }),
    );

  return roomInstallationSchedules.length === 0 ? null : roomInstallationSchedules.join('\n');
};

type InstallationScheduleParameters = {
  roomName: string;
  floorConstructionName: string;
  portNumbers: Array<number>;
  manifoldName: string;
  numberOfPipes: number;
  suppliedCoilLengthInMetres: number;
  cutLengthInMetres: number;
  pipeDiameterInMillimetres: number;
  pipeSpacingInMillimetres: number;
  flowRateInLitresPerMinute: number;
};

export const installationScheduleForRoom = ({
  roomName,
  floorConstructionName,
  portNumbers,
  manifoldName,
  numberOfPipes,
  suppliedCoilLengthInMetres,
  cutLengthInMetres,
  pipeDiameterInMillimetres,
  pipeSpacingInMillimetres,
  flowRateInLitresPerMinute,
}: InstallationScheduleParameters) => {
  return `${roomName} using ${floorConstructionName}
${manifoldName}
${portNumbers.length === 1 ? 'Port' : 'Ports'} ${portNumbers.join(', ')}
Supplied: ${
    pipeDiameterInMillimetres === 17
      ? 'See cutting plan'
      : `${numberOfPipes} x ${suppliedCoilLengthInMetres}m - ${pipeDiameterInMillimetres}mm ${
          numberOfPipes === 1 ? 'coil' : 'coils'
        }`
  }
Cut length: ${numberOfPipes} x ${cutLengthInMetres}m
Tube spacing: ${pipeSpacingInMillimetres}mm centres
Min. flow setting (per port): ${ceil(flowRateInLitresPerMinute / portNumbers.length, 1)} l/min
`;
};

export const heatSourceSizingText = (heatSource: UnitHeatSourceTypeCode | null, unitHeatLossInWatts: number) =>
  heatSource === 'HeatPump' ? heatPumpSizingText(unitHeatLossInWatts) : boilerSizingText(unitHeatLossInWatts);

const heatPumpSizingText = (unitHeatLossInWatts: number): string => {
  const buildingHeatLoadInKilowatts = round(unitHeatLossInWatts / 1000, 2);
  return `The building load used to size your Heat Pump is ${buildingHeatLoadInKilowatts}kW, which includes DHW requirements.

Pipe Distances have been estimated based on your building floor plans. Please use the relevant pipe sizing tables to calculate minimum pipe sizing based on your installation.

Please use the manifold dimension information provided on the relevant manifold pages for individual manifold space requirements

An expansion vessel has been included with your Heat Pump System, please install it in line with installation instructions.
`;
};

const boilerSizingText = (unitHeatLossInWatts: number): string => {
  const boilerHeatLoadInKilowatts = round(1.5 * (unitHeatLossInWatts / 1000), 2);

  return `The estimated boiler load for this system is ${boilerHeatLoadInKilowatts}kW, including a suitable response factor. Please allow an additional 3kW for any domestic hot water requirements.

Pipe Distances have been estimated based on your building floor plans. Please use the relevant pipe sizing tables to calculate minimum pipe sizing based on your installation.

Please use the manifold dimension information provided on the relevant manifold pages for individual manifold space requirements.

A filling loop and expansion vessel (these are not supplied) should be fitted, or a check should be made that the existing expansion vessel has enough capacity, to provide a minimum of &ltPlease enter size&gt litres expansion. This should be installed in line with relevant standards and installer recommendation.
`;
};
